import React, { useEffect, useState } from "react";
import { Col, Collapse, Row } from "antd";
import CardComponent from "@/components/card";
import Container from "@/components/container/Container";
import "@/styles/pages/plan.scss";
import ButtonComponent from "@/components/button/Button";
import IconTick from "@/components/icons/IconTick";
import { useDispatch, useSelector } from "react-redux";
import {
  getIsLoggedIn,
  getUserInfo,
  setCurrentForm,
  setFormProps,
  setModalOpen,
} from "@/reducers/commonSlice";
import {
  setCheckoutType,
  setIsCheckoutToShow,
  setIsProductRegisterSuccessToShow,
  setProductData,
  setRegisteProductId,
} from "@/reducers/checkoutSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getProductData } from "@/services/common/getProductData";
import { getProducts } from "@/services/services/getProducts";
import { getPurchaseDetails } from "@/services/students/getPurchaseDetails";
import { registerProducts } from "@/services/services/registerProducts";
import { STAGE_KEY } from "@/lib/constant";

const { Panel } = Collapse;

const Plan = () => {
  const [activePanel, setActivePanel] = useState("1");
  const isLoggedIn = useSelector(getIsLoggedIn);
  const userInfo = useSelector(getUserInfo);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  console.log(userInfo);

  // Map query parameter values to the panel keys.
  const panelMapping = {
    planning_to_apply_abroad: "1",
    already_applied_abroad: "2",
    ready_to_fly_abroad: "3",
    already_at_university_abroad: "4",
  };

  // Fetch products based on the user's stage or query parameter.
  const fetchProducts = async (stage) => {
    const fetchProductData = isLoggedIn ? getProducts : getProductData;
    return await fetchProductData(stage);
  };

  // Use TanStack Query to fetch products for each stage.
  const {
    data: preApplication,
    isLoading: isLoadingPreApplication,
    isError: isErrorPreApplication,
  } = useQuery({
    queryKey: [STAGE_KEY.PreApplication, isLoggedIn],
    queryFn: () => fetchProducts(STAGE_KEY.PreApplication),
  });

  const {
    data: alreadyApplied,
    isLoading: isLoadingAlreadyApplied,
    isError: isErrorAlreadyApplied,
  } = useQuery({
    queryKey: [STAGE_KEY.AlreadyApplied, isLoggedIn],
    queryFn: () => fetchProducts(STAGE_KEY.AlreadyApplied),
  });

  const {
    data: gotAdmission,
    isLoading: isLoadingGotAdmission,
    isError: isErrorGotAdmission,
  } = useQuery({
    queryKey: [STAGE_KEY.GotAdmission, isLoggedIn],
    queryFn: () => fetchProducts(STAGE_KEY.GotAdmission),
  });

  const {
    data: alreadyAtUniversity,
    isLoading: isLoadingAlreadyAtUniversity,
    isError: isErrorAlreadyAtUniversity,
  } = useQuery({
    queryKey: [STAGE_KEY.AlreadyAtUniversity, isLoggedIn],
    queryFn: () => fetchProducts(STAGE_KEY.AlreadyAtUniversity),
  });

  // Fetch purchased plans.
  const {
    data: purchasedPlan,
    isLoading: isLoadingPurchasedPlan,
    isError: isErrorPurchasedPlan,
  } = useQuery({
    queryKey: ["purchasedPlan", isLoggedIn],
    queryFn: getPurchaseDetails,
    enabled: isLoggedIn, // Only fetch if the user is logged in.
    select: (data) => data.map((x) => x.productId),
  });

  // Mutation for registering products.
  const registerProductMutation = useMutation({
    mutationFn: (productId) => registerProducts({ productId }),
    onSuccess: () => {
      dispatch(setCurrentForm("productSuccess"));
      dispatch(setModalOpen(true));
      dispatch(
        setFormProps({
          message: "Your interest was registered",
          subMessage: "Team will contact you shortly",
          onSubmit: () => {
            dispatch(setModalOpen(false));
            dispatch(setIsProductRegisterSuccessToShow(false));
          },
        })
      );
    },
    onError: (error) => {
      console.error("Registration failed:", error);
    },
  });

  // Handle adding a product to cart or registering interest.
  const handleAddToCart = (data) => {
    if (isLoggedIn) {
      if (data.action === "Register") {
        registerProductMutation.mutate(data.id);
      } else {
        dispatch(setCheckoutType(true));
        dispatch(setProductData(data));
        navigate("/checkout-preview");
      }
    } else {
      if (data.action === "Register") {
        dispatch(setIsProductRegisterSuccessToShow(true));
        dispatch(setRegisteProductId(data.id));
      } else {
        dispatch(setCheckoutType(true));
        dispatch(setIsCheckoutToShow(true));
        dispatch(setProductData(data));
      }
      dispatch(setModalOpen(true));
      dispatch(setCurrentForm("signIn"));
      dispatch(setCheckoutType(true));
      dispatch(setIsCheckoutToShow(true));
      dispatch(setProductData(data));
    }
  };

  // Set the active panel based on URL query or user's stage.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const planParam = searchParams.get("plan");

    if (planParam && panelMapping[planParam]) {
      setActivePanel(panelMapping[planParam]);
    } else {
      let stage = null;

      if (!isLoggedIn) {
        stage = localStorage.getItem("stage");
      } else {
        stage = userInfo?.stage
          ?.toLowerCase()
          .split(" ")
          .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
          .join(""); // Return type "PreApplication" || "AlreadyApplied" || "GotAdmission" || "AlreadyAtUniversity"
      }

      console.log({ stage });

      if (stage === STAGE_KEY.PreApplication) {
        setActivePanel("1");
      } else if (stage === STAGE_KEY.AlreadyApplied) {
        setActivePanel("2");
      } else if (stage === STAGE_KEY.GotAdmission) {
        setActivePanel("3");
      } else if (stage === STAGE_KEY.AlreadyAtUniversity) {
        setActivePanel("4");
      } else {
        setActivePanel("4");
      }
    }
  }, [location, userInfo]);

  // Scroll to the active panel when activePanel changes.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const planParam = searchParams.get("plan");
    if (!planParam) return;

    const timer = setTimeout(() => {
      const element = document.getElementById(activePanel);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [activePanel]);

  const handleScroll = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop,
        behavior: "smooth",
      });
    }
  };

  // Loading and error handling.
  if (
    isLoadingPreApplication ||
    isLoadingAlreadyApplied ||
    isLoadingGotAdmission ||
    isLoadingAlreadyAtUniversity ||
    isLoadingPurchasedPlan
  ) {
    return <div>Loading...</div>;
  }

  if (
    isErrorPreApplication ||
    isErrorAlreadyApplied ||
    isErrorGotAdmission ||
    isErrorAlreadyAtUniversity ||
    isErrorPurchasedPlan
  ) {
    return <div>Error fetching data</div>;
  }

  return (
    <div className="product-plans">
      <Container>
        <CardComponent>
          <div className="heading title-h5-1">Plans & Offerings</div>
          <div className="according">
            <Collapse
              accordion
              activeKey={activePanel}
              expandIconPosition="right"
              onChange={(key) => setActivePanel(key)}
              className="accordion"
            >
              <Panel
                style={{ scrollMarginTop: "150px" }}
                header="Planning to Apply Abroad"
                key="1"
                id="1"
                onClick={() => handleScroll("1")}
              >
                <PlanPanel
                  plans={preApplication}
                  purchasedPlan={purchasedPlan}
                  handleAddToCart={handleAddToCart}
                />
              </Panel>
              <Panel
                style={{ scrollMarginTop: "150px" }}
                header="Already Applied Abroad"
                key="2"
                id="2"
                onClick={() => handleScroll("2")}
              >
                <PlanPanel
                  plans={alreadyApplied}
                  purchasedPlan={purchasedPlan}
                  handleAddToCart={handleAddToCart}
                />
              </Panel>
              <Panel
                style={{ scrollMarginTop: "150px" }}
                header="Ready To Fly Abroad"
                key="3"
                id="3"
                onClick={() => handleScroll("3")}
              >
                <PlanPanel
                  plans={gotAdmission}
                  purchasedPlan={purchasedPlan}
                  handleAddToCart={handleAddToCart}
                />
              </Panel>
              <Panel
                style={{ scrollMarginTop: "150px" }}
                header="Already at University Abroad"
                key="4"
                id="4"
                onClick={() => handleScroll("4")}
              >
                <PlanPanel
                  plans={alreadyAtUniversity}
                  purchasedPlan={purchasedPlan}
                  handleAddToCart={handleAddToCart}
                />
              </Panel>
            </Collapse>
          </div>
        </CardComponent>
      </Container>
    </div>
  );
};

// Reusable PlanPanel component to avoid code duplication.
const PlanPanel = ({ plans, purchasedPlan, handleAddToCart }) => {
  const plansWithPurchaseStatus = plans.map((plan) => ({
    ...plan,
    isPurchased: plan.isPurchased || purchasedPlan?.includes(plan.id) || false,
  }));

  // Clear sorting implementation
  const sortedPlans = [...plansWithPurchaseStatus].sort((a, b) => {
    // 1) Always put highlighted items first
    if (a.highlighted && !b.highlighted) return -1;
    if (!a.highlighted && b.highlighted) return 1;

    // 2) Within the same highlighted-status group, put non-purchased/non-registered first
    const aOwned = a.isPurchased || a.isRegistered;
    const bOwned = b.isPurchased || b.isRegistered;
    if (aOwned && !bOwned) return 1; // a _after_ b
    if (!aOwned && bOwned) return -1; // a _before_ b

    // 3) Leave everything else in original order
    return 0;
  });

  console.log(
    "Sorted plans:",
    sortedPlans.map((p) => ({
      id: p.id,
      title: p.title,
      isPurchased: p.isPurchased,
      isRegistered: p.isRegistered,
      highlighted: p.highlighted,
    }))
  );

  const filteredPlans = sortedPlans.filter(
    (plan) =>
      plan.isPurchased || plan.isRegistered || purchasedPlan?.includes(plan.id) || plan.highlighted
  );
  // const plansToDisplay = filteredPlans.length > 0 ? filteredPlans : sortedPlans;
  const plansToDisplay = sortedPlans;

  return (
    <div className="content">
      <Row gutter={[22, 22]}>
        {plansToDisplay?.map((plan, index) => (
          <Col xs={24} sm={12} md={12} lg={8} xl={8} xxl={8} key={index}>
            <div
              className={`plan-card ${plan.highlighted ? "highlighted" : ""} ${
                plan.isPurchased || plan.isRegistered ? "blur-card" : ""
              }`}
            >
              <CardComponent>
                <Row style={{ height: "100%" }} justify="space-between" align="stretch">
                  <Col span={24}>
                    <div className="plan-heading title-h5-1">{plan.title}</div>
                    {plan.discount ? (
                      <div className="discount-chip body-b4">Save {plan.discount} %</div>
                    ) : null}
                    {plan.offerings?.map((item, idx) => (
                      <div className="including body-b4" key={idx}>
                        <IconTick />
                        <span style={{ width: "80%" }}>{item}</span>
                      </div>
                    ))}
                  </Col>
                  <Col span={24} style={{ marginTop: "auto" }}>
                    {plan.price ? <div className="price title-h5-1">£ {plan.price}</div> : null}
                    <div className="button">
                      <ButtonComponent
                        label={
                          plan.action !== "Register"
                            ? plan.isComplimentary
                              ? "Get for Free"
                              : "Buy Now"
                            : "Register your Interest"
                        }
                        varient="primary"
                        disabled={purchasedPlan?.includes(plan.id) || plan.isRegistered}
                        onClick={() => handleAddToCart(plan)}
                      />
                    </div>
                  </Col>
                </Row>
              </CardComponent>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default Plan;
